import { type FormEvent, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { api, type Domain } from '../api';
import { MailIcon, SendIcon } from '../components/icons';
import { ErrorNote, PageHeader } from '../components/ui';
import { useLoad } from '../hooks';

/** Always available: sends here never leave Vaartix and only reach the test addresses below. */
const TEST_DOMAIN = 'test.vaartix.com';
const TEST_ADDRESSES = ['delivered', 'bounced', 'complained'].map((n) => `${n}@${TEST_DOMAIN}`);
const LOCAL_PART = /^[A-Za-z0-9.!#$%&'*+/=?^_`{|}~-]+$/;

/** `Acme <hello@acme.in>`, quoting the name when it has characters an address header reserves. */
function fromAddress(name: string, local: string, domain: string) {
  const address = `${local}@${domain}`;
  const display = name.trim();
  if (!display) return address;
  return /[,;:<>@"()[\]\\]/.test(display)
    ? `"${display.replace(/["\\]/g, '')}" <${address}>`
    : `${display} <${address}>`;
}

/** Send one email from the dashboard, to check a domain or try the API without writing code. */
export function SendEmail() {
  const [params] = useSearchParams();
  const domains = useLoad(() => api<{ data: Domain[] }>('GET', '/domains'), []);
  const verified = (domains.data?.data ?? []).filter((d) => d.status === 'verified');

  const [name, setName] = useState('');
  const [local, setLocal] = useState('hello');
  // Until the user picks one: the domain from the link, else the first verified one, else test.
  const [picked, setPicked] = useState<string | null>(null);
  const requested = params.get('domain');
  const domain =
    picked ??
    (requested && verified.some((d) => d.name === requested)
      ? requested
      : (verified[0]?.name ?? TEST_DOMAIN));
  const isTest = domain === TEST_DOMAIN;
  const [to, setTo] = useState<string | null>(null);
  const recipient = to ?? (isTest ? (TEST_ADDRESSES[0] as string) : '');
  const [subject, setSubject] = useState('Test email from Vaartix');
  const [tab, setTab] = useState<'text' | 'html'>('text');
  const [text, setText] = useState('It works!');
  const [html, setHtml] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [sent, setSent] = useState<{ id: string; to: string } | null>(null);

  const localOk = LOCAL_PART.test(local.trim());
  const canSend =
    !busy &&
    localOk &&
    recipient.trim() !== '' &&
    subject.trim() !== '' &&
    (text.trim() || html.trim());

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!canSend) return;
    setBusy(true);
    setError(null);
    try {
      const body = {
        from: fromAddress(name, local.trim(), domain),
        to: recipient.trim(),
        subject: subject.trim(),
        ...(text.trim() ? { text } : {}),
        ...(html.trim() ? { html } : {}),
      };
      const { id } = await api<{ id: string }>('POST', '/emails', body);
      setSent({ id, to: body.to });
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <PageHeader
        crumbs={
          <>
            <Link to="/emails">Emails</Link>
            <span>/</span>
            <span>Send test email</span>
          </>
        }
        title="Send test email"
        description="Send one email from the dashboard to check a domain or see what the API does."
      />

      {sent ? (
        <div className="card">
          <div className="card-body sent-note">
            <span className="row-icon ok">
              <MailIcon />
            </span>
            <div>
              <strong>Email queued for {sent.to}.</strong>
              <p className="muted">
                Follow its delivery events on the email's page. Test addresses report back within a
                few seconds; real mailboxes usually within a minute.
              </p>
              <div className="sent-actions">
                <Link className="btn btn-primary btn-sm" to={`/emails/${sent.id}`}>
                  View email
                </Link>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => setSent(null)}
                >
                  Send another
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <form className="card send-form" onSubmit={submit}>
          <div className="card-body">
            <div className="field">
              <span className="label">From</span>
              <div className="from-row">
                <input
                  className="input"
                  aria-label="Sender name (optional)"
                  placeholder="Name (optional)"
                  maxLength={80}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
                <div className="from-address">
                  <input
                    className="input"
                    aria-label="Sender address, before the @"
                    value={local}
                    onChange={(e) => setLocal(e.target.value)}
                  />
                  <span className="at">@</span>
                  <select
                    className="input"
                    aria-label="Sending domain"
                    value={domain}
                    onChange={(e) => {
                      setPicked(e.target.value);
                      setTo(null);
                    }}
                  >
                    {verified.map((d) => (
                      <option key={d.id} value={d.name}>
                        {d.name}
                      </option>
                    ))}
                    <option value={TEST_DOMAIN}>{TEST_DOMAIN} (test mode)</option>
                  </select>
                </div>
              </div>
              {!localOk ? (
                <span className="hint warn">Use letters, numbers and . _ - + before the @.</span>
              ) : verified.length === 0 && !domains.loading ? (
                <span className="hint">
                  No verified domains yet, so only test mode is available.{' '}
                  <Link className="link" to="/domains/new">
                    Verify a domain
                  </Link>{' '}
                  to send real email.
                </span>
              ) : null}
            </div>

            <div className="field">
              <label htmlFor="send-to">To</label>
              <input
                id="send-to"
                className="input"
                type="email"
                placeholder={isTest ? TEST_ADDRESSES[0] : 'you@yourcompany.in'}
                value={recipient}
                onChange={(e) => setTo(e.target.value)}
              />
              {isTest ? (
                <span className="hint">
                  From {TEST_DOMAIN} you can only send to its test addresses:{' '}
                  {TEST_ADDRESSES.map((a, i) => (
                    <span key={a}>
                      {i ? ', ' : ''}
                      <button type="button" className="link-btn" onClick={() => setTo(a)}>
                        {a.split('@')[0]}@
                      </button>
                    </span>
                  ))}
                  . Each one simulates that outcome, so you can watch the events and webhooks.
                </span>
              ) : (
                <span className="hint">
                  Real recipients count toward your daily limit. Test addresses always work and
                  don't count.
                </span>
              )}
            </div>

            <div className="field">
              <label htmlFor="send-subject">Subject</label>
              <input
                id="send-subject"
                className="input"
                maxLength={200}
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
              />
            </div>

            <div className="field">
              <span className="label">Message</span>
              <div className="message-box">
                <div className="tabs" role="tablist">
                  <button
                    type="button"
                    role="tab"
                    aria-selected={tab === 'text'}
                    onClick={() => setTab('text')}
                  >
                    Plain text
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={tab === 'html'}
                    onClick={() => setTab('html')}
                  >
                    HTML (optional)
                  </button>
                </div>
                {tab === 'text' ? (
                  <textarea
                    className="input"
                    aria-label="Plain-text message"
                    rows={8}
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                  />
                ) : (
                  <textarea
                    className="input mono"
                    aria-label="HTML message"
                    rows={8}
                    placeholder="<p>It works!</p>"
                    value={html}
                    onChange={(e) => setHtml(e.target.value)}
                  />
                )}
              </div>
              <span className="hint">
                With both, mail apps show the HTML and fall back to the plain text.
              </span>
            </div>

            <ErrorNote error={error} />
          </div>
          <div className="card-foot">
            <span className="muted from-preview">{fromAddress(name, local.trim(), domain)}</span>
            <button type="submit" className="btn btn-primary" disabled={!canSend}>
              <SendIcon />
              {busy ? 'Sending…' : 'Send email'}
            </button>
          </div>
        </form>
      )}
    </>
  );
}
